import { useCallback, useEffect, useState } from "react";
import { api, asArray } from "@/lib/api";
import { toast } from "sonner";
import { fmtTime } from "@/lib/time";
import { Clock, Play, RefreshCw, ShieldCheck, Layers, CalendarClock } from "lucide-react";
import { KPI, StatusBadge } from "@/components/Primitives";

// Automation. Until this existed, every scan, detection and check in the product
// was a human pressing a button — survivable at ten endpoints, negligent at two
// hundred, because nobody finds out a backup has been failing for a week.

const inputCls =
  "bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-200 focus:border-zinc-600 outline-none";
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function describe(spec = {}) {
  if (spec.every_minutes) return `every ${spec.every_minutes} min`;
  const t = `${String(spec.at_hour ?? 2).padStart(2, "0")}:${String(spec.at_minute ?? 0).padStart(2, "0")}`;
  if (!spec.days_of_week?.length) return `daily at ${t}`;
  return `${spec.days_of_week.map((d) => DAYS[d]).join(", ")} at ${t}`;
}

export default function Automation() {
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [runs, setRuns] = useState([]);
  const [status, setStatus] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [win, setWin] = useState(null);
  const [rings, setRings] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [j, r, s, c] = await Promise.all([
        api.get("/scheduler/jobs"), api.get("/scheduler/runs?limit=60"),
        api.get("/scheduler/status"), api.get("/clients"),
      ]);
      setJobs(asArray(j.data)); setRuns(asArray(r.data));
      setStatus(s.data || {}); setClients(asArray(c.data));
      if (!clientId && asArray(c.data).length) setClientId(asArray(c.data)[0].id);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Could not load automation");
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const loadPolicy = useCallback(async () => {
    if (!clientId) return;
    try {
      const [w, r, a] = await Promise.all([
        api.get(`/clients/${clientId}/maintenance-window`),
        api.get(`/clients/${clientId}/patch-rings`),
        api.get(`/clients/${clientId}/patch-approval`),
      ]);
      setWin(w.data); setRings(asArray(r.data)); setApprovals(asArray(a.data));
    } catch (e) { toast.error("Could not load patch policy"); }
  }, [clientId]);
  useEffect(() => { if (tab === "patch") loadPolicy(); }, [tab, loadPolicy]);

  const runNow = async (job) => {
    setBusy(job.id);
    try {
      const { data } = await api.post(`/scheduler/jobs/${job.id}/run-now`, {});
      if (data.status === "ok") toast.success(`${job.name}: ${JSON.stringify(data.summary).slice(0, 90)}`);
      else toast.error(`${job.name} failed: ${data.error}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not run"); }
    finally { setBusy(""); }
  };

  const toggle = async (job) => {
    try {
      await api.patch(`/scheduler/jobs/${job.id}`, { enabled: !job.enabled });
      toast.success(job.enabled ? "Paused" : "Enabled");
      load();
    } catch (e) { toast.error("Could not change the job"); }
  };

  const saveWindow = async () => {
    try {
      const { data } = await api.put(`/clients/${clientId}/maintenance-window`, win);
      setWin(data);
      toast.success(data.open_now ? "Saved — the window is open right now" : "Saved");
    } catch (e) { toast.error("Could not save the window"); }
  };

  const saveRings = async () => {
    try {
      const { data } = await api.put(`/clients/${clientId}/patch-rings`, { rings });
      toast.success(`Assigned ${data.assigned} device(s) across ${data.rings} rings`);
      loadPolicy();
    } catch (e) { toast.error("Could not save the rings"); }
  };

  const approve = async () => {
    try {
      await api.post(`/clients/${clientId}/patch-approval`, { severities: ["critical", "important"] });
      toast.success("Approved — rings will release it over the next few days");
      loadPolicy();
    } catch (e) { toast.error("Could not approve"); }
  };

  return (
    <div className="p-6" data-testid="automation-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Clock size={18} className="text-zinc-400" />
        <div>
          <h1 className="text-lg font-display font-semibold text-zinc-50">Automation</h1>
          <div className="text-xs text-zinc-500">Scheduled work, its history, and how patches are released</div>
        </div>
        <button onClick={load} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-300 text-sm border border-zinc-800">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {status && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
          <KPI label="Scheduler" value={status.running ? "Running" : "STOPPED"}
            tone={status.running ? "good" : "bad"} testid="sc-running" />
          <KPI label="Jobs enabled" value={`${status.enabled ?? 0}/${status.jobs ?? 0}`} testid="sc-jobs" />
          <KPI label="Runs 24h" value={status.runs_24h ?? 0} testid="sc-runs" />
          <KPI label="Errors 24h" value={status.errors_24h ?? 0}
            tone={status.errors_24h ? "bad" : "good"} testid="sc-errors" />
          <KPI label="Next due" value={status.next_due ? fmtTime(status.next_due) : "—"} testid="sc-next" />
        </div>
      )}
      {status && !status.running && (
        <div className="mt-3 border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">
          The scheduler loop is not running. Nothing is being checked automatically.
        </div>
      )}

      <div className="flex gap-1 mt-6 border-b border-zinc-900">
        {[["jobs", "Scheduled jobs", Clock], ["runs", "Run history", ShieldCheck],
          ["patch", "Patch release", Layers]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`auto-tab-${k}`}
            className={`flex items-center gap-2 px-3.5 py-2 text-[13px] border-b-2 -mb-px ${tab === k
              ? "border-blue-500 text-zinc-100" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            <Icon size={14} /> {l}
          </button>
        ))}
      </div>

      {err && <div className="mt-4 border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">{err}</div>}

      {tab === "jobs" && (
        <div className="mt-4 bg-zinc-950 border border-zinc-900 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-900">
                <th className="text-left px-4 py-2.5">Job</th>
                <th className="text-left px-4 py-2.5">Schedule</th>
                <th className="text-left px-4 py-2.5">Last run</th>
                <th className="text-left px-4 py-2.5">Result</th>
                <th className="text-left px-4 py-2.5">Next</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} data-testid={`job-${j.key}`}
                  className={`border-b border-zinc-900/70 hover:bg-zinc-900/40 align-top ${j.enabled ? "" : "opacity-55"}`}>
                  <td className="px-4 py-2.5 text-zinc-200">
                    {j.name}
                    {j.description && <div className="text-[11.5px] text-zinc-600 mt-0.5 max-w-md">{j.description}</div>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[12px] text-zinc-400">{describe(j.spec)}</td>
                  <td className="px-4 py-2.5 font-mono text-[11.5px] text-zinc-500">
                    {j.last_run_at ? fmtTime(j.last_run_at) : "never"}
                  </td>
                  <td className="px-4 py-2.5">
                    {j.last_status
                      ? <StatusBadge status={j.last_status === "ok" ? "success" : "failed"} />
                      : <span className="text-zinc-600 text-xs">—</span>}
                    {j.last_summary && (
                      <div className="text-[11px] text-zinc-600 mt-1 font-mono max-w-sm break-words">
                        {Object.entries(j.last_summary).slice(0, 4).map(([k, v]) =>
                          `${k}=${Array.isArray(v) ? v.length : v}`).join(" · ")}
                      </div>
                    )}
                    {j.last_error && <div className="text-[11px] text-red-400 mt-1 max-w-sm">{j.last_error}</div>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[11.5px] text-zinc-500">
                    {j.enabled ? (j.next_run_at ? fmtTime(j.next_run_at) : "—") : "paused"}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => runNow(j)} disabled={busy === j.id} data-testid={`run-${j.key}`}
                        title="Run now"
                        className="p-1.5 rounded hover:bg-zinc-800 text-emerald-400 disabled:opacity-40">
                        <Play size={14} />
                      </button>
                      <button onClick={() => toggle(j)} data-testid={`toggle-${j.key}`}
                        className={`rounded-full transition-colors relative shrink-0 ${j.enabled ? "bg-emerald-500/70" : "bg-zinc-700"}`}
                        style={{ height: 20, width: 36 }} title={j.enabled ? "Pause" : "Enable"}>
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${j.enabled ? "left-[18px]" : "left-0.5"}`} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "runs" && (
        <div className="mt-4 bg-zinc-950 border border-zinc-900 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-900">
                <th className="text-left px-4 py-2.5">Started</th>
                <th className="text-left px-4 py-2.5">Job</th>
                <th className="text-left px-4 py-2.5">Trigger</th>
                <th className="text-left px-4 py-2.5">Took</th>
                <th className="text-left px-4 py-2.5">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {!runs.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-600 text-sm">No runs recorded yet.</td></tr>}
              {runs.map((r) => (
                <tr key={r.id} className="border-b border-zinc-900/70 hover:bg-zinc-900/40 align-top">
                  <td className="px-4 py-2.5 font-mono text-[11.5px] text-zinc-500">{fmtTime(r.started_at)}</td>
                  <td className="px-4 py-2.5 text-zinc-300">{r.job_key}</td>
                  <td className="px-4 py-2.5 text-[12px] text-zinc-500">{r.triggered_by}</td>
                  <td className="px-4 py-2.5 font-mono text-[12px] text-zinc-400">{r.ms}ms</td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={r.status === "ok" ? "success" : "failed"} />
                    <div className="text-[11px] text-zinc-600 mt-1 font-mono max-w-lg break-words">
                      {r.error || (r.summary ? JSON.stringify(r.summary) : "")}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "patch" && (
        <div className="mt-4 space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Client</span>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls}>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
            <div className="flex items-center gap-2 mb-1">
              <CalendarClock size={15} className="text-zinc-400" />
              <h3 className="text-sm font-medium text-zinc-100">Maintenance window</h3>
              {win && (
                <span className={`ml-2 text-[11px] font-mono px-2 py-0.5 rounded border ${win.open_now
                  ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
                  : "text-zinc-500 border-zinc-800 bg-zinc-900"}`}>
                  {win.open_now ? "OPEN NOW" : "closed"}
                </span>
              )}
            </div>
            <p className="text-[13px] text-zinc-500 mb-4 max-w-2xl">
              Nothing is installed outside this window. Evaluated in the client&rsquo;s local
              time, not the server&rsquo;s — a 01:00 window in Manila is not 01:00 UTC.
            </p>
            {win && (
              <>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {DAYS.map((d, i) => {
                    const on = (win.days_of_week || []).includes(i);
                    return (
                      <button key={d} onClick={() => setWin({
                        ...win,
                        days_of_week: on ? win.days_of_week.filter((x) => x !== i)
                          : [...(win.days_of_week || []), i].sort(),
                      })}
                        className={`px-2.5 py-1 rounded text-xs border ${on
                          ? "bg-blue-600/20 border-blue-500/40 text-blue-200"
                          : "bg-zinc-900 border-zinc-800 text-zinc-500"}`}>{d}</button>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
                    From
                    <input type="number" min="0" max="23" value={win.start_hour}
                      onChange={(e) => setWin({ ...win, start_hour: Number(e.target.value) })}
                      className={`${inputCls} block mt-1 w-20`} />
                  </label>
                  <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
                    To
                    <input type="number" min="0" max="23" value={win.end_hour}
                      onChange={(e) => setWin({ ...win, end_hour: Number(e.target.value) })}
                      className={`${inputCls} block mt-1 w-20`} />
                  </label>
                  <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
                    Client UTC offset (min)
                    <input type="number" value={win.tz_offset_minutes}
                      onChange={(e) => setWin({ ...win, tz_offset_minutes: Number(e.target.value) })}
                      className={`${inputCls} block mt-1 w-28`} />
                  </label>
                  <button onClick={saveWindow} data-testid="save-window"
                    className="px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm">Save window</button>
                </div>
              </>
            )}
          </div>

          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
            <div className="flex items-center gap-2 mb-1">
              <Layers size={15} className="text-zinc-400" />
              <h3 className="text-sm font-medium text-zinc-100">Patch rings</h3>
            </div>
            <p className="text-[13px] text-zinc-500 mb-4 max-w-2xl">
              Devices are placed in rings deterministically, so &ldquo;pilot first&rdquo;
              means the same machines each time. Each ring waits its delay after approval
              before anything installs.
            </p>
            <div className="space-y-2">
              {rings.map((r, i) => (
                <div key={r.name} className="flex flex-wrap items-center gap-3 text-sm">
                  <input value={r.name} onChange={(e) => {
                    const n = [...rings]; n[i] = { ...r, name: e.target.value }; setRings(n);
                  }} className={`${inputCls} w-32`} />
                  <label className="text-[11px] text-zinc-500 font-mono">delay
                    <input type="number" min="0" value={r.delay_days} onChange={(e) => {
                      const n = [...rings]; n[i] = { ...r, delay_days: Number(e.target.value) }; setRings(n);
                    }} className={`${inputCls} ml-1.5 w-16`} /> d</label>
                  <label className="text-[11px] text-zinc-500 font-mono">share
                    <input type="number" min="0" max="100" value={r.share_pct} onChange={(e) => {
                      const n = [...rings]; n[i] = { ...r, share_pct: Number(e.target.value) }; setRings(n);
                    }} className={`${inputCls} ml-1.5 w-16`} /> %</label>
                  <span className="text-[12px] text-zinc-400 font-mono">{r.devices ?? 0} device(s)</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={saveRings} data-testid="save-rings"
                className="px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm">Save &amp; reassign</button>
              <button onClick={approve} data-testid="approve-patches"
                className="px-4 py-1.5 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-sm">
                Approve current updates
              </button>
            </div>
            {approvals.length > 0 && (
              <div className="mt-4 border-t border-zinc-900 pt-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Recent approvals</div>
                {approvals.slice(0, 4).map((a) => (
                  <div key={a.id} className="text-[12.5px] text-zinc-400 font-mono">
                    {fmtTime(a.approved_at)} · {(a.severities || []).join(", ")} · by {a.approved_by}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
